import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { ChevronRight, Plus } from 'lucide-react';
import { Avatar, Badge, Button, Card, PageLoader, Typography } from '../ui';
import { AvatarPhotoEditor } from '../AvatarPhotoEditor';
import { contactThreadPath, isRecipientId, setContactAvatar, useContactEntryQuery, type ContactId, type WebContact } from '../../../lib/contact-book';
import {
  channelLabelKey,
  contactDisplayName,
  longRelativeTime,
  shortRelativeTime,
  sourceLabelKey,
  threadChannel,
  threadTitle,
} from '../../../lib/contact-rows';
import { contactScreen, stageBiValue, threadStage, threadTime } from '../../../lib/outreach-stage';
import { ContactReadError } from './ContactReadError';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { noteEventOnContact } from '../../../lib/outreach-lane';
import { ownerNoteActionVisibleOnContact } from '../../../lib/owner-note-refresh';
import { useBasePath } from '../../../lib/use-base-path';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useAgentName } from '../../../lib/use-agent-name';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import { useContactSeen } from '../../../lib/use-contact-seen';
import { AddChannelDialog } from './AddChannelDialog';
import { ChannelChip } from './ChannelChip';
import { ContactOutreachViewer } from './ContactOutreachViewer';
import { CustomerHistorySection } from './CustomerHistorySection';
import { StageDot } from './StageDot';
import { StageLine } from './StageLine';
import { StageQuestion } from './StageCard';
import { PersonCardSections } from './PersonCardSections';
import { TellAgentComposer } from '../outreach/TellAgentComposer';
import { ContactTimelineTranscript } from '../outreach/OutreachThreadTranscript';
import { timelineThreads } from '../../../lib/outreach-thread-view';
import { personCardModel } from '../../../lib/person-card';
import styles from './contacts.module.css';

/**
 * One contact — the Outreach detail screen (Figma web `17937:269038`, native `17937:264777`).
 *
 * Desktop is two columns: an identity card (avatar · name · the "Tell {agent} about {name}" composer) on
 * the left, and the details column on the right — Channels, "What The Maestro knows", "Needs you", and
 * the info rows. Mobile-web drops the identity card and centres the avatar/name/composer above the same
 * details, matching the native phones. Shared 1:1 with iOS `ContactDetailScreen` and Android `ContactDetailScreen`.
 *
 * Everything on screen arrives in the one contact-book read filtered to this person — no second call.
 */
export function ContactDetailBody({ id }: { id: ContactId }) {
  const { t, i18n } = useTranslation();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  // The real query signals, not `useContactEntry`'s data-only view: that one reports `undefined` for a
  // FAILED read exactly as for a pending one, so `if (!entry) return <PageLoader />` below painted a
  // spinner that never resolved whenever the contact-book read failed — and did the same for a read that
  // succeeded with no contact in it. The trap is documented on `useContactEntryQuery`; the viewer sheet
  // already read it this way and these two pages did not.
  const { entry, isLoading, isError, refetch } = useContactEntryQuery(id);
  const seen = useContactSeen();
  const seenMark = seen.mark;
  const [imageError, setImageError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  // The person id when this is a person, empty for an address-only contact — the seen-mark and avatar
  // seed use whichever the identity carries, and the person-only edits (photo override, add-channel)
  // are hidden for a recipient (#10660): they write to the People service BY person id.
  const recipient = isRecipientId(id);
  const personId = recipient ? '' : id.personId;
  const seedId = recipient ? id.recipientId : id.personId;
  useEffect(() => {
    if (entry) seenMark(seedId);
  }, [entry, seedId, seenMark]);

  // A conversation row opens THAT outreach in the viewer, in place. The header there is a no-op — we
  // are already on the contact screen (`onOpenContact` omitted).
  const [viewerOutreachId, setViewerOutreachId] = useState<string | null>(null);

  // Channels sharing a title (e.g. two "Phone" entries) collapse into ONE card that lists every value,
  // with a single "Preferred" badge if any of them is preferred. Values are deduped so an identical
  // number isn't shown twice.
  const channelGroups = useMemo(() => {
    const order: string[] = [];
    const byLabel = new Map<string, { label: string; values: string[]; preferred: boolean }>();
    for (const channel of entry?.channels ?? []) {
      const label = channel.label ?? '';
      const group = byLabel.get(label);
      if (group) {
        if (channel.value && !group.values.includes(channel.value)) group.values.push(channel.value);
        group.preferred = group.preferred || !!channel.preferred;
      } else {
        byLabel.set(label, { label, values: channel.value ? [channel.value] : [], preferred: !!channel.preferred });
        order.push(label);
      }
    }
    return order.map((label) => byLabel.get(label)!);
  }, [entry]);

  // The person card's structured content (P1.16) — the same model iOS and Android build from this entry.
  // `now` is read whenever the entry changes (the memo is keyed on it), not on every render: "in 3 days" moves on
  // with the next read of the person (a refetch, an edit), so a tab left open overnight catches up then.
  const cardModel = useMemo(() => personCardModel(entry ?? {}, new Date()), [entry]);
  // The "Full conversation" timeline's threads — every thread with this person, from the SAME person-scoped
  // read (threads arrive whole on it, `messages` included), so the timeline costs no request of its own.
  const timeline = useMemo(() => timelineThreads(entry ?? {}), [entry]);

  if (isLoading) return <PageLoader />;
  if (!entry) return <ContactReadError canRetry={isError} onRetry={refetch} />;

  const card = entry.contact;
  const displayName = contactDisplayName(card, entry.channels, t('contacts.row.unnamed'));
  // The contact screen (Q10): the open-question cards on top, then ONE list of every conversation with
  // this person, by stage rank then recency — `contactScreen`, shared 1:1 with both phones.
  const screen = contactScreen(entry, displayName);

  // The photo override is a person-only write; an address-only contact shows a plain avatar (#10660).
  const avatar = recipient ? (
    <Avatar initials={avatarInitials(displayName)} photoUrl={card?.effectiveAvatarUrl} size={48} color={avatarSlotFor(seedId || displayName)} />
  ) : (
    <AvatarPhotoEditor
      initials={avatarInitials(displayName)}
      photoUrl={card?.effectiveAvatarUrl}
      size={48}
      color={avatarSlotFor(seedId || displayName)}
      canRemove={!!card?.effectiveAvatarUrl}
      onSave={async (url) => {
        const ok = await setContactAvatar(personId, url);
        if (!ok) throw new Error('contact avatar save failed');
      }}
      onError={setImageError}
      page="contact_detail"
      buttonName="edit_contact_photo"
    />
  );

  // "Tell {agent} about {name}" — the contact-level composer that replaced "Reach out" (outreach visibility
  // phase 4). The note goes to the owner's own agent, which starts or continues a conversation with this
  // person or acts on it; nothing navigates. It is addressed BY PERSON (`/api/tell-agent-contact` takes a
  // person id, validated in the caller's own People store), so an address-only contact — no person id —
  // gets none: there is no person to address it by, and its threads each carry their own composer.
  const reachOut = personId ? (
    <TellAgentComposer
      scope={{ kind: 'contact', personId, name: displayName }}
      page="contact_detail"
      refetch={refetch}
      isSettled={(noteId) => ownerNoteActionVisibleOnContact(entry, noteId)}
      serverEventFor={(noteId) => noteEventOnContact(entry, noteId)}
    />
  ) : null;

  // Identity: centred avatar/name/button on mobile-web (like native); a left-aligned card on desktop.
  const identity = narrow ? (
    <div className={styles.identityNarrow}>
      {avatar}
      {imageError && <p className={styles.imageError}>{imageError}</p>}
      <Typography variant="h1" color="primary" className={styles.identityNameNarrow}>
        {displayName}
      </Typography>
      {reachOut}
    </div>
  ) : (
    <Card variant="sunken" size="lg" className={styles.identityCard}>
      <div className={styles.identityRow}>
        {avatar}
        <Typography variant="h2" color="primary" className={styles.identityName}>
          {displayName}
        </Typography>
      </div>
      {imageError && <p className={styles.imageError}>{imageError}</p>}
      {reachOut}
    </Card>
  );

  const addedFromKey = sourceLabelKey(entry.source) ? `contacts.detail.addedFrom.${entry.source!.trim().toLowerCase()}` : null;

  const details = (
    <div className={styles.detailSections}>
      {/* The open questions, on top (Q10, rule 2): ONE "Needs you" heading, then per question the bare
          shared block (question + chips) — no "is asking you" line. The card is NOT a button, so the
          block's chips can sit in it. */}
      {screen.questionThreads.length > 0 ? (
        <div className={styles.detailSection}>
          <Typography variant="h3" color="primary">
            {t('contacts.detail.needsYou')}
          </Typography>
          {screen.questionThreads.map(({ thread }) => (
            <Card key={thread.needsYou?.decisionId ?? thread.outreachId} variant="sunken" size="lg">
              <StageQuestion thread={thread} page="contact_detail" header="none" />
            </Card>
          ))}
        </div>
      ) : null}

      {/* Channels */}
      <div className={styles.detailSection}>
        <div className={styles.sectionHead}>
          <Typography variant="h3" color="primary">
            {t('contacts.detail.channels')}
          </Typography>
          {/* Adding a channel is a person-only write — hidden for an address-only contact (#10660). */}
          {!recipient ? (
            <Button
              variant="text"
              size="small"
              startIcon={<Plus size={16} aria-hidden />}
              onClick={() => {
                logPageClickBi({ page_name: 'contact_detail', button_name: 'add_channel' });
                setAddOpen(true);
              }}
            >
              {t('common.add')}
            </Button>
          ) : null}
        </div>
        <div className={styles.channelCards}>
          {channelGroups.map((group) => (
            <Card key={group.label} variant="sunken" size="sm" className={styles.channelCard}>
              {group.preferred && narrow ? (
                <span className={styles.channelBadgeTop}>
                  <Badge variant="agent-profession" size="tiny" label={t('contacts.detail.preferred')} />
                </span>
              ) : null}
              <div className={styles.channelRow}>
                <span className={styles.channelText}>
                  <Typography variant="h4" color="primary">
                    {group.label}
                  </Typography>
                  {group.values.map((value) => (
                    <Typography key={value} variant="p2" color="secondary" className={styles.channelValue}>
                      {value}
                    </Typography>
                  ))}
                </span>
                {group.preferred && !narrow ? (
                  // Top-align the badge once the card has more than two rows (label + 2+ values); a
                  // single-value card keeps it vertically centred, per Figma.
                  <span className={group.values.length > 1 ? styles.channelBadgeRightTop : undefined}>
                    <Badge variant="agent-profession" size="tiny" label={t('contacts.detail.preferred')} />
                  </span>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
        {!recipient ? <AddChannelDialog personId={personId} open={addOpen} onClose={() => setAddOpen(false)} /> : null}
        {viewerOutreachId ? (
          <ContactOutreachViewer
            personId={personId}
            recipientId={recipient ? id.recipientId : undefined}
            outreachId={viewerOutreachId}
            open
            onClose={() => setViewerOutreachId(null)}
          />
        ) : null}
      </div>

      {/* Customer history — this person as a customer of the owner's Wix sites (orders, bookings, invoices, Wix
          Inbox), right after Channels, for a person and an address-only contact alike. Looked up by the entry's own
          email and phone values; draws nothing until there is something to draw. Shared 1:1 with iOS
          `ContactDetailScreen` and Android `ContactDetail.kt` (native-apps/shared/customer-history/contract.md). */}
      <CustomerHistorySection channels={entry.channels} contactName={displayName} />

      {/* Relationship · Important dates · Social profiles · Work — person-only, and only from a server that
          serves them (an older entry shows none of these sections rather than empty ones). */}
      {!recipient && cardModel.structured ? <PersonCardSections personId={personId} model={cardModel} /> : null}

      {/* What The Maestro knows */}
      {cardModel.facts.length ? (
        <div className={styles.detailSection}>
          <Typography variant="h3" color="primary">
            {t('contacts.detail.knows', { maestroAgentName: agent.name })}
          </Typography>
          <Card variant="sunken" size="lg">
            <ul className={styles.bullets}>
              {cardModel.facts.map((fact) => (
                <li key={fact.text} className={styles.bullet}>
                  <span className={styles.bulletDot} aria-hidden />
                  <span className={styles.channelText}>
                    <Typography variant="p2" color="primary">
                      {fact.text}
                    </Typography>
                    {/* A no-suggest fact (health, relationship trouble, religion) is kept but never used to
                        suggest anything unless the user raises it — the card says so. */}
                    {fact.noSuggestBadge ? (
                      <span className={styles.personCardBadge}>
                        <Badge label={t('people.card.noSuggest')} />
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      ) : null}

      {/* Conversations — ONE list (Q10), titled "Conversations · N" (N = `uniqueThreads`), sorted by stage
          rank then recency. Each row: the dot, the thread's title, "{group} · {line}", the channel. */}
      {screen.rows.length > 0 ? (
        <div className={styles.detailSection}>
          <Typography variant="h3" color="primary">
            {t(screen.title.key, screen.title.params)}
          </Typography>
          <Card variant="sunken" size="lg">
            <div className={styles.needsRows}>
              {screen.rows.map(({ thread }) => {
                const outreachId = thread.outreachId ?? '';
                if (!outreachId) return null;
                return (
                  <Link
                    key={outreachId}
                    // `contactThreadPath` is RELATIVE — a raw <Link> must carry the active mount prefix
                    // or the deep link (and a modified/new-tab click) breaks on /orion-web and /symphony-web.
                    href={`${basePath}${contactThreadPath(id, outreachId)}`}
                    className={styles.needsRow}
                    onClick={(e) => {
                      // Plain click opens the viewer in place; a modified click (new tab / cmd) still
                      // resolves the deep-link route, which renders the same conversation full-page.
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                      e.preventDefault();
                      logPageClickBi(
                        { page_name: 'contact_detail', button_name: 'open_outreach' },
                        { additionalFields: { stage: stageBiValue(threadStage(thread)) } },
                      );
                      setViewerOutreachId(outreachId);
                    }}
                  >
                    <span className={styles.outreachRowMain}>
                      <span className={styles.stageNameRow}>
                        <StageDot stage={threadStage(thread)} />
                        <Typography variant="h4" color="primary">
                          {threadTitle(thread, i18n.language)}
                        </Typography>
                      </span>
                      <StageLine thread={thread} contactName={displayName} withGroup className={styles.outreachRowPreview} />
                      <ChannelChip channel={threadChannel(thread)} outlined />
                    </span>
                    <span className={styles.needsEnd}>
                      <Typography variant="p3" color="secondary">
                        {shortRelativeTime(threadTime(thread), undefined, t('contacts.time.now'))}
                      </Typography>
                      <ChevronRight size={24} className="dir-flip" aria-hidden />
                    </span>
                  </Link>
                );
              })}
            </div>
          </Card>
        </div>
      ) : null}

      {/* Full conversation — every thread with this person merged into ONE chronological transcript, through
          the same bubbles a thread draws (`ContactTimelineTranscript`, model `contactTimelineRows`). A topic row
          ("Lunch · Sep 8") opens each switch of thread; pressing it opens that thread in the viewer, where its
          status, decision and composer live. The Conversations list above stays: it is the stage-ranked index
          of what is live, this is the history. Shared 1:1 with iOS `ContactDetailScreen` and Android
          `ContactDetail.kt`. */}
      {timeline.threads.some((thread) => thread.messages.length > 0) ? (
        <div className={styles.detailSection}>
          <Typography variant="h3" color="primary">
            {t('outreach.timeline.title')}
          </Typography>
          <Card variant="sunken" size="lg">
            <ContactTimelineTranscript
              threads={timeline.threads}
              source={timeline.source}
              contactName={displayName}
              maestroAgentName={agent.name}
              page="contact_detail"
              contactPhoto={card?.effectiveAvatarUrl}
              avatarKey={seedId || displayName}
              onOpenThread={setViewerOutreachId}
            />
          </Card>
        </div>
      ) : null}

      {/* Info rows */}
      <div className={styles.infoRows}>
        {entry.source ? (
          <Card variant="sunken" size="sm" className={styles.infoCard}>
            <Typography variant="h4" color="primary">
              {addedFromKey ? t(addedFromKey) : t('contacts.detail.addedFrom.other', { source: entry.source })}
            </Typography>
          </Card>
        ) : null}
        {card?.lastTouchAt ? (
          <Card variant="sunken" size="sm" className={styles.infoCard}>
            <Typography variant="h4" color="primary">
              {t('contacts.detail.lastContacted')}
            </Typography>
            <Typography variant="p2" color="secondary">
              {longRelativeTime(card.lastTouchAt)}
            </Typography>
          </Card>
        ) : null}
      </div>
    </div>
  );

  return narrow ? (
    <div className={styles.detailRoot}>
      {identity}
      {details}
    </div>
  ) : (
    <div className={styles.detailTwoCol}>
      <div className={styles.identityCol}>{identity}</div>
      <div className={styles.detailsCol}>{details}</div>
    </div>
  );
}

export type { WebContact };
